import styled from 'styled-components/macro';

import { ColorType } from 'common/types/theme';
import { colorType } from 'components/common';

const GenerateButtonStyles = (
  bg: string,
  hoverBg: string,
  color?: string,
  borderColor = 'transparent',
  hoverColor?: string,
  disabledBgColor?: string,
  disabledColor?: string,
) => `
  background-color:${bg};

  :enabled {
    &:hover{
      background-color:${hoverBg};
      color: ${hoverColor};
    }
  }
  &:focus-visible {
    border:1px solid ${bg};
  }
  &:disabled {
    color: ${disabledColor};
    background-color:${disabledBgColor};
    border: 1px solid #ededed;
  }
  color: ${color};
  border:1px solid ${borderColor};
  `;

export function pickStyle(color: string, colors: Record<ColorType, string>) {
  switch (color) {
    case 'red':
      return GenerateButtonStyles(colors['negative_default'], colors['negative_dark']);
    case 'dark-red':
      return GenerateButtonStyles(colors['negative_dark_0'], colors['negative_dark_0']);
    case 'red-outline':
      return GenerateButtonStyles(
        colors['white'],
        colors['negative_dark_0'],
        colors['negative_dark_0'],
        colors['negative_dark_0'],
        colors['white'],
      );

    case 'blue':
      return GenerateButtonStyles(colors['primary'], colors['primary_90']);
    case 'light-blue':
      return GenerateButtonStyles(colors['primary_10'], colors['primary_30'], colors['primary']);
    case 'ghost-blue':
      return GenerateButtonStyles(
        colors['transparent'],
        colors['transparent'],
        colors['primary_dark'],
        colors['transparent'],
        colors['primary'],
      );
    case 'ghost-black':
      return GenerateButtonStyles(
        colors['transparent'],
        colors['transparent'],
        colors['white'],
        colors['transparent'],
        colors['black_20'],
      );

    case 'yellow':
      return GenerateButtonStyles(
        colors['warning_default'],
        colors['warning_default'],
        colors['black_5'],
      );

    case 'green':
      return GenerateButtonStyles(
        colors['positive_default'],
        colors['positive_dark'],
        colors['white'],
      );
    case 'light-green':
      return GenerateButtonStyles(
        colors['positive_light_0'],
        colors['positive_dark'],
        colors['positive_dark'],
        'transparent',
        colors['white'],
      );

    case 'black':
      return GenerateButtonStyles(
        colors['black_100'],
        colors['black_80'],
        colors['black_60'],
        colors['black_80'],
      );
    case 'transparent':
      return GenerateButtonStyles(
        colors['transparent'],
        colors['transparent'],
        colors['black_60'],
        colors['transparent'],
        colors['black_80'],
      );

    case 'white':
      return GenerateButtonStyles(
        colors['white'],
        colors['black_40'],
        colors['black_80'],
        colors['black_40'],
        colors['black_80'],
        colors['black_5'],
        colors['black_40'],
      );

    case 'white-green':
      return GenerateButtonStyles(
        colors['white'],
        colors['white'],
        colors['positive_dark'],
        colors['positive_dark'],
      );
    case 'white-red':
      return GenerateButtonStyles(
        colors['white'],
        colors['white'],
        colors['negative_dark_0'],
        colors['negative_dark_0'],
      );

    case 'grey':
      return GenerateButtonStyles(colors['black_10'], colors['black_60'], colors['black_80']);
    case 'light-grey':
      return GenerateButtonStyles(colors['black_5'], colors['black_10'], colors['black_80']);
    case 'dark-grey':
      return GenerateButtonStyles(colors['black_80'], colors['black_60'], colors['white']);
    case 'orange':
      return GenerateButtonStyles(colors['ag_primary'], colors['ag_primary_dark']);
    case 'airbnb-pink':
      return GenerateButtonStyles(colors['airbnb_primary'], colors['airbnb_primary_dark']);
    case 'purple':
      return GenerateButtonStyles(
        colors['purple_50'],
        colors['purple_75'],
        colors['white'],
        colors['transparent'],
        colors['white'],
      );
    case 'soon-blue':
      return GenerateButtonStyles(
        colors['primary_10'],
        colors['primary_20'],
        colors['primary_dark'],
        colors['primary_dark'],
        colors['primary'],
      );
    case 'primary-outline':
      return GenerateButtonStyles(
        colors['transparent'],
        colors['black_10'],
        colors['primary_dark'],
        colors['primary'],
        colors['primary'],
      );
    case 'ghost-purple':
      return GenerateButtonStyles(
        colors['transparent'],
        colors['transparent'],
        colors['purple_75'],
        colors['transparent'],
        colors['primary'],
      );

    default:
      return null;
  }
}

export const DefaultButton = styled.button<{
  width?: string;
  height?: string;
  margin?: string;
  padding?: string;
  borderRadius?: string;
  svgHoverColor?: string;
  color: colorType;
  borderTopLeftRadius?: boolean;
  borderBottomLeftRadius?: boolean;
  borderTopRightRadius?: boolean;
  visibility?: string;
  gap?: string;
  justifyContent?: string;
  textTransform?: string;
  fontSize?: string;
}>`
  position: relative;

  display: flex;
  align-items: center;
  justify-content: ${({ justifyContent }) => justifyContent || 'center'};

  width: ${({ width }) => width || 'auto'};
  height: ${({ height }) => height || '2.5rem'};

  gap: ${({ gap }) => gap || '0'};

  margin: ${({ margin }) => margin || '0'};
  padding: ${({ padding }) => padding || '0 1rem'};
  border-radius: ${({ borderRadius }) => borderRadius || '0.5rem'};
  overflow: visible;

  ${({ borderTopLeftRadius }) => borderTopLeftRadius && ` border-top-left-radius: 0;`}
  ${({ borderTopRightRadius }) => borderTopRightRadius && ` border-top-right-radius: 0;`}
  ${({ borderBottomLeftRadius }) => borderBottomLeftRadius && ` border-bottom-left-radius: 0;`}

  ${({ visibility }) => visibility && `visibility: ${visibility}`};

  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme, fontSize }) => (fontSize ? fontSize : theme.fontSize.xs)};
  font-family: ${({ theme }) => theme.fontFamily.semiBold};

  letter-spacing: ${({ theme }) => theme.letterSpacing.md};

  outline: none;

  text-transform: ${({ textTransform }) => textTransform && textTransform};

  opacity: ${({ disabled }) => disabled && '50%'};

  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  > * {
    pointer-events: ${({ disabled }) => disabled && 'none'};
  }
  ${({ theme, color }) => color && pickStyle(color, theme.colors)}
  &:focus-visible {
    box-shadow: ${({ theme }) =>
      '0 0 3px 0 ' + theme.colors.black + ',inset 0 0 3px 0 ' + theme.colors.black};
  }
  :enabled {
    &:hover > div > svg path {
      stroke: ${({ svgHoverColor }) => svgHoverColor};
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0 0.5rem;
  }
`;
